import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { AppContext } from "Context/AppContext";
import { ArsContext } from "./Contexts/ArsContext";
import { toast } from "Components/DesignSystem/Toast/useToast";

import ArsLogsTable from "./Components/ArsLogsTable";
import NoDataComponent from "Components/Common/NoDataComponent";
import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement";
import ConfirmModal from "./Components/ConfirmModal";
import Paginator from "Components/Common/PaginatorWithApi";
import PromoteConfirmDialog from "./Components/PromoteConfirmDialog";

import { createRecommenderModel, generateRecommendations, generateRecommender, getRecommenderLogs, promoteModels } from "./ARSServices/ARSServices";
import { checkNotGeneratedStatus } from "./ARSHelper";

import { ARS_GENERATE_MESSAGE, ARS_GENERATION_SUCCESS, GENERATE, PROCEED, PROMOTE, COMMEND, CREATE, TITLE, RECOMMENDER_READ, RECOMMENDER_CREATE, RECOMMENDER_PROMOTE, RECOMMENDER_GENERATE, RECOMMENDER_UPDATE, RECOMMENDER_DELETE, RECOMMENDER_UPLOAD } from "./ARSConstants";
import GenerateStatus from "./Components/GenerateStatus";
import ReloadButton from "./Components/ReloadButton";
import HIstoryTable from "./Components/HIstoryTable";
import { TCreateModel } from "../ABTesting/types/ab";
import CreateModelDialog from "./Components/CreateModelDialog";
import { RecommenderLogsResponse } from "./Types";
import { GearIcon, ColoredMenuDownIcon, AddIcon, UploadIcon } from "assests/GmsIcons";
import Button from "Components/DesignSystem/Button/Button";
import MerchantSettingsModal from "./Components/MerchantSettingsModal";
import AppletModal from "./Components/AppletModal";
import UploadExternalModelDialog from "./Components/UploadExternalModelDialog";

const ARSComponent = () => {
  const appData = useContext(AppContext);
  const { logsData, setLogsData, checkGenerateStatus, checkPromoteeStatus, showStatus, showPromote } = useContext(ArsContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [viewLog, setViewLog] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [promoteModal, setPromoteModal] = useState<boolean>(false);

  const [showApplet, setShowApplet] = useState<boolean>(false);
  const [isInitialArsLoad, setIsInitialArsLoad] = useState<boolean>(true);

  const [showMerchantSettings, setShowMerchantSettings] = useState<boolean>(false);

  const [promoteRecommenderModel, setPromoteRecommenderModal] = useState<boolean>(false);
  const [logId, setLogId] = useState<string>('');
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<RecommenderLogsResponse[]>([]);
  const [paginationParams, setPaginationParams] = useState<TPaginationParams>({ per_page: 10, page: 1, sort_by: "updatedOn", sort_by_order: "desc" });

  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createModelLoader, setCreateModelLoader] = useState<boolean>(false);
  const [promoteLoading, setPromoteLoading] = useState<boolean>(false);
  const [showCreateMenu, setShowCreateMenu] = useState<boolean>(false);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const createMenuRef = useRef<HTMLDivElement | null>(null);
  const openDialog = () => setShowCreateDialog(true);
  const closeDialog = () => setShowCreateDialog(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target as Node)) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const createPermission = appData.pm.checkPermission(RECOMMENDER_CREATE);
  const readPermission = appData.pm.checkPermission(RECOMMENDER_READ);
  const updatePermission = appData.pm.checkPermission(RECOMMENDER_UPDATE);
  const deletePermission = appData.pm.checkPermission(RECOMMENDER_DELETE);
  const promotePermission = appData.pm.checkPermission(RECOMMENDER_PROMOTE);
  const generatePermission = appData.pm.checkPermission(RECOMMENDER_GENERATE);
  const externalModelUploadPermission = appData.pm.checkPermission(RECOMMENDER_UPLOAD);
  const industryId = appData?.get_selected_industry()?.id;
  const channelKey = appData?.channelKey;

  useEffect(() => {
    if ((location.state as any)?.openCreate && createPermission) {
      openDialog();
      navigate({
  pathname: location.pathname
}, {
  state: {},
  replace: true
});
    }
  }, []);

  useEffect(() => {
    if (!readPermission) {
      navigate("/workspace/module/permission/denied");
      return;
    }
    if (appData?.channelKey) {
      getRecLogs();
      const intervalId = setInterval(getRecLogs, 2 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [appData.channelKey, paginationParams]);

  useEffect(() => {
    if (!isInitialArsLoad && !showStatus) {
      // Wait 2 seconds before refreshing table after generation completes
      const timeoutId = setTimeout(() => {
        getRecLogs();
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [showStatus])

  useEffect(() => {
    if (!isInitialArsLoad && !showPromote) {
      // Wait 2 seconds before refreshing table after promotion completes
      const timeoutId = setTimeout(() => {
        getRecLogs();
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [showPromote])

  const onFieldSubmit = async (modelName: string, type: "Co-seller" | "Substitution", defaultSubmodel: boolean) => {
    if (!industryId || !channelKey) return
    if (!createPermission) {
      toast.warning('You do not have permission to create model');
      return;
    }
    const model: TCreateModel = { industry_id: industryId, channel_id: channelKey, model_name: modelName, type: type, require_default_submodel: defaultSubmodel };
    try {
      setCreateModelLoader(true);
      const res = await createRecommenderModel(model);
      if (res?.done) {
        toast.success("Model created successfully");
        getRecLogs();
        if (res?.model_id) navigate(`/gms/commend/model/${res?.model_id}`);
      }
      else {
        toast.info(res?.message);
      }
    } catch (error) {
      console.log(error);
      setCreateModelLoader(false);
    } finally {
      setCreateModelLoader(false);
      closeDialog();
    }
  }

  const getRecLogs = useCallback(async () => {
    if (!appData?.channelKey) return;

    if (isInitialArsLoad) {
      setLoading(true);
    }

    try {
      const res = await getRecommenderLogs(appData.channelKey, paginationParams);
      if (res?.done) {
        const logs = res?.data ?? [];
        setLogsData(logs);
        setTotalRecords(res?.total ?? 0);
      } else {
        toast.error(res?.message);
      }

    } catch (error) {
      console.error('Error fetching recommender logs:', error);
      toast.error("Failed to fetch recommender logs. Please try again.");
    } finally {
      setLoading(false);
      if (isInitialArsLoad) {
        setIsInitialArsLoad(false);
      }
    }
  }, [appData?.channelKey, paginationParams, setLogsData]);

  const canPromoteModel = (model: RecommenderLogsResponse): boolean => {
    const genStatus = model.generation_status.status;
    const promoStatus = model.promotion_status.status;

    // Must be generated first
    if (genStatus !== "Generated") return false;

    // Can promote if not promoted yet OR if promoted but regenerated
    return (
      promoStatus === "Not Promoted" ||
      (promoStatus === "Promoted" && model.promotion_status.regenerated === true)
    );
  };

  const canGenerateModel = (model: RecommenderLogsResponse): boolean => {
    const genStatus = model.generation_status.status;
    const promoStatus = model.promotion_status.status;

    // Cannot generate if currently generating, promoting, or in validating or Validation Failed 
    return genStatus !== "Generating" && genStatus !== "Validating" && genStatus !== "Failed" && promoStatus !== "Promoting";
  };

  const callGenerateAPI = useCallback(async () => {
    try {
      const response = await generateRecommendations('ARS', appData?.userName ?? '', appData?.channelKey ?? '');
      if (response?.done) {
        toast.success(ARS_GENERATION_SUCCESS);
      } else {
        throw new Error('Generate API call failed');
      }
    } catch (error) {
      console.error("Generate API call error:", error);
      toast.error("Failed to generate recommendations. Please try again.");
    }
  }, [appData?.userName, appData?.channelKey]);

  const generateARS = useCallback(async (modelIds: string[]) => {
    try {
      setLoading(true);
      const response = await generateRecommender(modelIds, appData?.channelKey ?? '');
      if (response?.done) {
        checkGenerateStatus(response?.job_id ?? '');
      } else {
        toast.success(response?.message ?? 'Something went wrong');
      }
    } catch (error) {
      console.error("ARS generation error:", error);
      toast.error("Failed to generate ARS. Please try again.");
    } finally {
      setLoading(false);
      setPromoteModal(false);
      setSelectedItems([]);
    }
  }, [appData?.channelKey, getRecLogs]);

  const handleOnConfirm = useCallback(() => {
    callGenerateAPI();
    closeConfirmModal();
  }, [callGenerateAPI]);

  const closeConfirmModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const closePromoteRecommenderModal = useCallback(() => {
    setPromoteRecommenderModal(false);
    setSelectedItems([]);
  }, []);

  const promoteSelectedModels = useCallback(async () => {
    setPromoteLoading(true);
    const modelIds = selectedItems.map(item => item.model_id);
    const response = await promoteModels({
      channel: appData?.channelKey ?? '',
      models: modelIds
    });
    if (response?.done) {
      checkPromoteeStatus(response?.job_id ?? '');
      getRecLogs();
    } else {
      toast.error(response?.status ?? 'Promotion failed');
    }
    setPromoteLoading(false);
    closePromoteRecommenderModal();
  }, [appData?.channelKey, selectedItems, getRecLogs, checkPromoteeStatus, closePromoteRecommenderModal]);

  const handleSelectItemChange = useCallback((item: RecommenderLogsResponse) => {
    setSelectedItems(prevItems => prevItems.filter(i => i.model_id !== item.model_id));
  }, []);

  const openPromoteRecommenderModal = useCallback(() => {
    if (!checkNotGeneratedStatus(selectedItems)) {
      setPromoteRecommenderModal(true);
    } else {
      toast.warning("You've selected a Not Generated model, please unselect it.");
    }
  }, [selectedItems]);

  const closeApplet = useCallback(() => {
    setShowApplet(false);
  }, []);

  const updatePageSize = useCallback((pageSize: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      page: 1,
      per_page: pageSize,
    }));
  }, []);

  const handlePageChange = useCallback((pageNumber: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      page: pageNumber,
    }));
  }, []);


  const isPromoteButtonDisabled = useMemo(() => {
    // Check permission first
    if (!promotePermission) return true;

    // Check selection count
    if (selectedItems.length === 0 || selectedItems.length > 3) return true;

    // ALL selected models must be eligible for promotion
    return !selectedItems.every(item => canPromoteModel(item));
  }, [selectedItems, promotePermission]);

  const isGenerateButtonDisabled = useMemo(() => {
    // Check permission first
    if (!generatePermission) return true;

    // Check selection count
    if (selectedItems.length === 0 || selectedItems.length > 3) return true;

    // Disable if any selected model is external
    if (selectedItems.some(item => item.is_external)) return true;

    // ALL selected models must be eligible for generation
    return !selectedItems.every(item => canGenerateModel(item));
  }, [selectedItems, generatePermission]);


  return (
    <>
      <div id="ars-header" data-testid="ars-header" className={`tw-flex tw-justify-between tw-border-b tw-border-[#D1D1D6] tw-pb-2`}>
        <div className={`tw-flex tw-items-center tw-text-xs tw-gap-1`}>
          <label id="ars-title" data-testid="ars-title" className={`tw-text-[#091E42] tw-text-[24px] tw-font-semibold`}>{COMMEND}{showHistory && " - History"}</label>
          {updatePermission && (
            <button id="ars-merchant-settings-btn" data-testid="ars-merchant-settings-btn" aria-label="Merchant settings" className="tw-text-primary tw-flex tw-items-center tw-justify-center tw-p-1" onClick={() => setShowMerchantSettings(true)}>
              <GearIcon />
            </button>
          )}
        </div>
        <div id="ars-header-actions" className={`tw-flex tw-items-center tw-gap-4`}>
          <GenerateStatus isGenerate={showStatus} isPromote={showPromote} />
          {!loading && logsData?.length > 0 ? (
            <Button
              id="ars-btn-toggle-history"
              data-testid="ars-toggle-history-btn"
              variant="tertiary"
              onClick={() => setShowHistory(prev => !prev)}
            >
              {showHistory ? 'Commend' : 'History'}
            </Button>
          ) : null}
          {!showHistory ? <ReloadButton loading={loading} onClick={getRecLogs} /> : null}
          <Button
            id="ars-btn-applet"
            data-testid="ars-applet-btn"
            variant="secondary"
            onClick={() => setShowApplet(prev => !prev)}
          >
            Applet
          </Button>
          <Button
            id="ars-btn-promote"
            data-testid="ars-promote-btn"
            variant="primary"
            disabled={isPromoteButtonDisabled}
            onClick={() => {
              if (!promotePermission) {
                toast.warning('You do not have permission to promote model');
                return;
              }
              openPromoteRecommenderModal();
            }}
          >
            {PROMOTE}
          </Button>
          <Button
            id="ars-btn-generate"
            data-testid="ars-generate-btn"
            variant="primary"
            disabled={isGenerateButtonDisabled}
            onClick={() => {
              if (!generatePermission) {
                toast.warning('You do not have permission to generate model');
                return;
              }
              setPromoteModal(true)
            }}
          >
            {GENERATE}
          </Button>
          <div className="tw-relative" ref={createMenuRef}>
            <Button
              id="ars-btn-create"
              data-testid="ars-create-btn"
              variant="primary"
              disabled={!createPermission}
              icon={externalModelUploadPermission ? <ColoredMenuDownIcon /> : undefined}
              showIcon={externalModelUploadPermission}
              iconPosition="end"
              onClick={() => {
                if (!createPermission) {
                  toast.warning('You do not have permission to create model');
                  return;
                }
                if (externalModelUploadPermission) {
                  setShowCreateMenu(prev => !prev);
                } else {
                  openDialog();
                }
              }}
            >
              Create
            </Button>
            {externalModelUploadPermission && showCreateMenu && (
              <div
                id="ars-create-menu"
                className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-bg-white tw-shadow-md tw-rounded tw-border tw-border-[#D1D1D6] tw-z-50 tw-min-w-[210px]"
              >
                <button data-testid="ars-index-button-1"
                  id="ars-btn-create-new-model"
                  className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-w-full tw-text-left hover:tw-bg-blue-100 tw-text-sm tw-text-[#172B4D]"
                  onClick={() => { openDialog(); setShowCreateMenu(false); }}
                >
                  <AddIcon color={"#172B4D"} />
                  <span>New Model</span>
                </button>
                <button data-testid="ars-index-button-2"
                  id="ars-btn-upload-external-model"
                  className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-w-full tw-text-left hover:tw-bg-blue-100 tw-text-sm tw-text-[#172B4D]"
                  onClick={() => { setShowUploadDialog(true); setShowCreateMenu(false); }}
                >
                  <UploadIcon />
                  <span>Upload External Model</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!showHistory && loading && <TableSkeletonElement />}

      {!loading && logsData?.length > 0 && !showHistory ? (
        <ArsLogsTable
          data={logsData}
          setViewLog={setViewLog}
          setLogId={setLogId}
          getRecLogs={getRecLogs}
          setLoading={setLoading}
          promoteModal={promoteModal}
          setPromoteModal={setPromoteModal}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          generateRecommender={generateARS}
          paginationParams={paginationParams}
          setPaginationParams={setPaginationParams}
          createPermission={createPermission}
          updatePermission={updatePermission}
          deletePermission={deletePermission}
        />
      ) : null}

      {showHistory ? <HIstoryTable merchantId={appData?.channelKey ?? ''} /> : null}

      {!loading && logsData?.length === 0 && (
        <div id="ars-empty-state" className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
          <NoDataComponent title="No models created" />
          <button data-testid="ars-index-button-3"
            id="ars-empty-btn-create-model"
            className="button-style tw-w-max tw-mt-4"
            disabled={!createPermission}
            onClick={() => {
              if (!createPermission) {
                toast.warning('You do not have permission to create model');
                return;
              }
              openDialog();
            }}
          >
            Create Model
          </button>
        </div>
      )}

      {!loading && logsData?.length > 0 && !showHistory ? (
        <div id="ars-pagination" className="tw-py-2">
          <div className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}>
            <div className={`tw-flex tw-items-center tw-space-x-3`}>
              <span>Show</span>
              <select data-testid="ars-index-select-4"
                id="ars-select-page-size"
                className={`tw-border focus:tw-outline-none tw-p-1`}
                style={{ width: "80px" }}
                aria-label="Records on page select"
                value={paginationParams.per_page}
                onChange={(e) => updatePageSize(parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
            <Paginator
              onPageChange={handlePageChange}
              recordPerPage={paginationParams.per_page}
              presentPage={paginationParams.page}
              totalRecords={totalRecords}
            />
          </div>
        </div>
      ) : null}

      <ConfirmModal
        id="ars-modal-confirm-generate"
        showModal={showModal}
        closeModal={closeConfirmModal}
        onConfirm={handleOnConfirm}
        primaryMessage={ARS_GENERATE_MESSAGE}
        secondaryMessage={PROCEED}
      />

      {showApplet && (
        <AppletModal
          id="ars-modal-applet"
          showApplet={showApplet}
          closeApplet={closeApplet}
          channelId={appData?.channelKey ?? ''}
          onSaveSuccess={getRecLogs}
        />
      )}

      <PromoteConfirmDialog
        id="ars-modal-promote"
        showModal={promoteRecommenderModel}
        closeModal={closePromoteRecommenderModal}
        onConfirm={promoteSelectedModels}
        loading={promoteLoading}
        selectedItems={selectedItems}
        handleSelectItemChange={handleSelectItemChange}
      />

      {showCreateDialog ?
        <CreateModelDialog
          id="ars-modal-create-model"
          dialogHeader={CREATE}
          labelName={TITLE}
          onClose={closeDialog}
          onFieldSubmit={onFieldSubmit}
          showIndicator={createModelLoader}
        /> : null}

      {showMerchantSettings ?
        <MerchantSettingsModal
          id="ars-modal-merchant-settings"
          showMerchantSettings={showMerchantSettings}
          closeMerchantSettings={() => setShowMerchantSettings(false)}
          channelId={channelKey}
          environmentKey={channelKey}
        /> : null}

      {showUploadDialog &&
        <UploadExternalModelDialog
          id="ars-modal-upload-external-model"
          onClose={() => setShowUploadDialog(false)}
          onUploadSuccess={getRecLogs}
          channelId={channelKey ?? ''}
          industryId={industryId ?? ''}
        />}
    </>
  );
};

export default ARSComponent;